/**
 * Dashboard routes.
 *
 * Screens are no longer gated here by plan name. Which modules a screen needs
 * lives in internals/config/moduleAccess.ts, and the server says which modules
 * a shop has — see docs/pos-subscription-stripe.md.
 */
import UserView from "./modules/user/views/UserView";
import TableView from "./modules/table/views/TableView";
import BrandView from "./modules/brand/views/BrandView";
import OfferView from "./modules/offer/views/OfferView";
import StockIntakeListView from "./modules/stock/views/StockIntakeListView";
import StockLevelsView from "./modules/stockLevels/views/StockLevelsView";
import ProductView from "./modules/product/views/Product";
import ReportView from "./modules/report/views/ReportView";
import ServiceView from "./modules/service/views/ServiceView";
import CompanyInfo from "./modules/setting/views/CompanyInfo";
import PersonalInfo from "./modules/setting/views/PersonalInfo";
import RegisterInfo from "./modules/setting/views/RegisterInfo";
import DownloadView from "./modules/download/views/DownloadView";
import CustomerView from "./modules/customer/views/CustomerView";
import CategoryView from "./modules/category/views/CategoryView";
import SupplierView from "./modules/supplier/views/SupplierView";
import EmployeeView from "./modules/employee/views/EmployeeView";
import DiscountView from "./modules/discount/views/DiscountView";
import ModifierView from "./modules/modifier/views/ModifierView";
import WarrantyView from "./modules/warranty/views/WarrantyView";
import DashboardView from "./modules/dashboard/views/DashboardView";
import TransactionView from "./modules/transaction/views/TransactionView";
import SubscriptionPlans from "./modules/setting/views/SubscriptionPlans";
import UberEatsInfo from "./modules/setting/views/UberEatsInfo";
import MenuViews from "./modules/menu/views/MenuView";
import CompReportView from "./modules/complementary/views/CompReportView";
import ContactsView from "./modules/complementary/views/ContactsView";
import MembersView from "./modules/netlyPay/views/MembersView";
import ImportMembersView from "./modules/netlyPay/views/ImportMembersView";
import PaymentRequestsView from "./modules/netlyPay/views/PaymentRequestsView";
import ApprovalsView from "./modules/netlyPay/views/ApprovalsView";
import NetlyPaySettingsView from "./modules/netlyPay/views/NetlyPaySettingsView";

const sidebarRoutes = [
  {
    path: "/dashboard",
    name: "Dashboard",
    rtlName: "Overview",
    component: DashboardView,
    layout: "/admin",
  },
  {
    name: "Products",
    path: "/product",
    layout: "/admin",
    rtlName: "Product Management",
    subCategory: [
      {
        path: "/product",
        name: "Product",
        rtlName: "Product Management",
        component: ProductView,
        layout: "/admin",
      },
      {
        path: "/category",
        name: "Category",
        rtlName: "Category Management",
        component: CategoryView,
        layout: "/admin",
      },
      {
        path: "/brand",
        name: "Brand",
        rtlName: "Category Management",
        component: BrandView,
        layout: "/admin",
      },
      {
        path: "/supplier",
        name: "Supplier",
        rtlName: "Supplier Management",
        component: SupplierView,
        layout: "/admin",
      },
      {
        path: "/modifiers",
        name: "Modifier",
        rtlName: "Modifiers Management",
        component: ModifierView,
        layout: "/admin",
      },
      {
        path: "/menu",
        name: "Menu",
        rtlName: "Menu Management",
        component: MenuViews,
        layout: "/admin",
      },
      {
        path: "/stock",
        name: "Stock Intakes",
        rtlName: "Stock Intakes",
        component: StockIntakeListView,
      },
      {
        // Sits beside the ingredient purchasing screen under Stock. Not
        // gated in moduleAccess, matching /stock — a shop that cannot see
        // its own counts is worse than one that sees an empty table.
        path: "/stock-levels",
        name: "Stock Levels",
        rtlName: "Stock Levels",
        component: StockLevelsView,
        layout: "/admin",
      },
      {
        path: "/warranty",
        name: "Warranty",
        rtlName: "Warranty Management",
        component: WarrantyView,
        layout: "/admin",
      },
    ],
  },
  {
    path: "/service",
    name: "Services",
    rtlName: "Services",
    component: ServiceView,
    layout: "/admin",
  },
  {
    path: "/courses",
    name: "Courses",
    rtlName: "Courses",
    component: ServiceView,
    layout: "/admin",
  },
  {
    path: "/employees",
    name: "Employees",
    rtlName: "Employees",
    component: EmployeeView,
    layout: "/admin",
  },
  {
    path: "/customers",
    name: "Customers",
    rtlName: "Customers",
    component: CustomerView,
    layout: "/admin",
  },
  {
    // Netly Pay. Not gated in moduleAccess: a shop with no workspace sees the
    // Enable screen, which is the honest state and needs no entitlement (D59).
    //
    // Import sits under Members rather than beside it: the design brief's
    // sidebar has one PEOPLE entry for Netly Pay, and importing is something
    // an owner does to the member list, not a place they work in.
    path: "/members",
    name: "Members",
    rtlName: "Members",
    component: MembersView,
    layout: "/admin",
    subCategory: [
      {
        path: "/members",
        name: "Members",
        rtlName: "Members",
        component: MembersView,
        layout: "/admin",
      },
      {
        path: "/members/import",
        name: "Import",
        rtlName: "Import Members",
        component: ImportMembersView,
        layout: "/admin",
      },
    ],
  },
  {
    // BILLING. Both screens show for every shop during launch (D59).
    path: "/payment-requests",
    name: "Payment requests",
    rtlName: "Payment Requests",
    component: PaymentRequestsView,
    layout: "/admin",
  },
  {
    path: "/approvals",
    name: "Approvals",
    rtlName: "Payment Approvals",
    component: ApprovalsView,
    layout: "/admin",
  },
  {
    path: "/users",
    name: "Users",
    rtlName: "Users",
    component: UserView,
    layout: "/admin",
  },
  {
    path: "/report",
    name: "Reports",
    rtlName: "Reports",
    component: ReportView,
    layout: "/admin",
  },
  {
    path: "/transaction",
    name: "Transaction",
    rtlName: "Transaction",
    component: TransactionView,
    layout: "/admin",
  },
  {
    name: "Complementary",
    path: "/complementary",
    layout: "/admin",
    rtlName: "Complementary Management",
    subCategory: [
      {
        path: "/complementary/report",
        name: "Comp Report",
        rtlName: "Complementary Report",
        component: CompReportView,
        layout: "/admin",
      },
      {
        path: "/complementary/contacts",
        name: "Contacts",
        rtlName: "Contacts",
        component: ContactsView,
        layout: "/admin",
      },
    ],
  },
  {
    path: "/tables",
    name: "Tables",
    rtlName: "Tables",
    component: TableView,
    layout: "/admin",
  },
  {
    path: "/discounts",
    name: "Discounts",
    rtlName: "Discounts",
    component: DiscountView,
    layout: "/admin",
  },
  {
    path: "/offer",
    name: "Offer",
    rtlName: "Offer",
    component: OfferView,
    layout: "/admin",
  },
  {
    path: "/download",
    name: "Download",
    rtlName: "Download",
    component: DownloadView,
    layout: "/admin",
  },
  {
    layout: "/admin",
    path: "/personal-info",
    name: "Settings",
    rtlName: "Personal Information",
    subCategory: [
      {
        layout: "/admin",
        path: "/personal-info",
        component: PersonalInfo,
        name: "Personal",
        rtlName: "Personal Information",
      },
      {
        layout: "/admin",
        path: "/company-info",
        component: CompanyInfo,
        name: "Company",
        rtlName: "Company Information",
      },
      {
        layout: "/admin",
        name: "Register",
        path: "/register-info",
        component: RegisterInfo,
        rtlName: "Register Information",
      },
      {
        layout: "/admin",
        name: "Subscription Plans",
        path: "/subscription-plans",
        component: SubscriptionPlans,
        rtlName: "Subscription Plans",
      },
      {
        layout: "/admin",
        name: "Integrations",
        path: "/integrations",
        component: UberEatsInfo,
        rtlName: "Integrations",
      },
      {
        // Netly Pay's own settings, reached from Settings rather than from a
        // sidebar entry of their own (DESIGN-BRIEF.md, SYSTEM group).
        layout: "/admin",
        name: "Netly Pay",
        path: "/netly-pay/settings",
        component: NetlyPaySettingsView,
        rtlName: "Netly Pay Settings",
      },
    ],
  },
];
export default sidebarRoutes;
